import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { User, Settings, LogOut, Shield, Mail, Calendar, ChevronDown } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { useToast } from '../../hooks/use-toast';

const UserMenu = ({ className = "" }) => {
  const { user, logout, getCurrentUserRole, currentOrganization } = useAuth();
  const { toast } = useToast();
  const [showMenu, setShowMenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const buttonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to logout properly",
        variant: "destructive",
      });
    }
  };

  const userRole = getCurrentUserRole();
  const roleLabels = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    manager: 'Manager',
    member: 'Member'
  };

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right
      });
    }
  }, [showMenu]);

  if (!user) return null;

  return (
    <div className={`relative ${className}`}>
      <Button
        ref={buttonRef}
        variant="ghost"
        className="flex items-center space-x-2 h-9 px-3"
        onClick={() => setShowMenu(!showMenu)}
      >
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={user.full_name || user.username}
            className="w-6 h-6 rounded-full"
          />
        ) : (
          <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
            <User className="w-3 h-3 text-gray-600" />
          </div>
        )}
        <span className="hidden sm:block text-sm font-medium">
          {user.full_name || user.username}
        </span>
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </Button>

      {showMenu && createPortal(
        <div 
          className="fixed w-64 z-[9999]" 
          style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
        >
          <Card>
            <CardContent className="p-0">
              {/* User Info Header */}
              <div className="p-3 border-b">
                <div className="flex items-center space-x-3">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.full_name || user.username}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {user.full_name || user.username}
                    </div>
                    <div className="text-sm text-gray-600 truncate">
                      {user.email}
                    </div>
                    {currentOrganization && userRole && (
                      <div className="flex items-center space-x-2 mt-1">
                        <Shield className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          {roleLabels[userRole] || userRole}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowProfile(true);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <User className="w-4 h-4" />
                  <span>Profile Settings</span>
                </button>
                
                <button
                  onClick={() => {
                    // Navigate to settings page
                    window.location.href = '/settings';
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Settings className="w-4 h-4" />
                  <span>Organization Settings</span>
                </button>

                <button
                  onClick={() => {
                    // Navigate to dashboard
                    window.location.href = '/';
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Dashboard</span>
                </button>
              </div>

              {/* Logout */}
              <div className="border-t py-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>,
        document.body
      )}

      {/* Click outside to close */}
      {showMenu && createPortal(
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => setShowMenu(false)}
        />,
        document.body
      )}

      {/* Profile Dialog */}
      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Profile Settings</DialogTitle>
            <DialogDescription>
              Manage your account information and preferences.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.full_name || user.username}
                  className="w-16 h-16 rounded-full"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center">
                  <User className="w-8 h-8 text-gray-600" />
                </div>
              )}
              <div>
                <h3 className="font-medium text-lg">{user.full_name || user.username}</h3>
                <p className="text-gray-600">{user.email}</p>
                <p className="text-sm text-gray-500">
                  Member since {new Date(user.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="font-medium text-gray-700">Username</label>
                <p className="text-gray-900">{user.username}</p>
              </div>
              <div>
                <label className="font-medium text-gray-700">Account Status</label>
                <p className="text-gray-900">
                  {user.is_verified ? (
                    <span className="text-green-600">Verified</span>
                  ) : (
                    <span className="text-yellow-600">Unverified</span>
                  )}
                </p>
              </div>
            </div>

            {currentOrganization && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-2">Current Organization</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Name:</span> {currentOrganization.name}</p>
                  <p><span className="font-medium">Role:</span> {roleLabels[userRole] || userRole}</p>
                  <p><span className="font-medium">Joined:</span> Member</p>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button onClick={() => setShowProfile(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserMenu;